// =============================================================================
// PREVIOUS IMPLEMENTATION — kept verbatim below for reference, intentionally
// inert. Line comments rather than a /* */ block: the original text contains
// JSDoc terminators, which would close a block comment early.
//
// The active implementation follows after this block. It is this same file
// plus a `desktop` namespace in both dictionaries, added for the three-column
// onboarding workspace at /tenant. Nothing above it is live code.
// =============================================================================

// /**
//  * Copy for the tenant onboarding steps.
//  *
//  * The French is lifted from the French mockup board in the technical
//  * specification, which is the client's own wording — prefer it over a fresh
//  * translation. Strings with no counterpart on the board (validation errors,
//  * screen-reader labels, page titles) are translated here and marked.
//  *
//  * French typography: a no-break space precedes `! ? :` so the punctuation
//  * cannot be orphaned onto the next line.
//  */
//
// const en = {
//   common: {
//     back: "Go back",
//     language: "Language",
//     /** Screen-reader only; the visible indicator is "2 / 7". */
//     stepOf: (step: number, total: number) => `Step ${step} of ${total}`,
//   },
//
//   welcome: {
//     documentTitle: "Welcome home | Europea",
//     titleLine1: "Welcome",
//     titleLine2: "home",
//     subtitle:
//       "We’re delighted to have you. Complete your onboarding in a few simple steps.",
//     bookingVia: "Your booking via",
//     apartment: "Apartment",
//     checkIn: "Check-in",
//     checkOut: "Check-out",
//     cta: "START MY ONBOARDING",
//   },
//
//   verify: {
//     documentTitle: "Verify your identity | Europea",
//     title: "Verify your identity",
//     subtitle: "This step is required to finalize your rental agreement.",
//     notice:
//       "Your documents are encrypted and securely deleted automatically after verification (within 7 days).",
//     promptLine1: "Drag & drop your ID document",
//     promptLine2: "or click to upload",
//     hintLine1: "Accepted documents: Passport or ID card",
//     hintLine2: (maxMb: number) => `(PDF, JPG or PNG – Max. ${maxMb} MB)`,
//     cta: "I’VE UPLOADED MY DOCUMENT",
//     footnote: "100% secure & confidential",
//   },
//
//   contract: {
//     documentTitle: "Review & sign your contract | Europea",
//     title: "Review & sign your contract",
//     subtitle: "Please review the documents below carefully.",
//     mainContractTitle: "Furnished rental agreement",
//     mainContractCaption: "Main contract",
//     attestationTitle: "Stay attestation (optional)",
//     attestationCaption: "For mobility lease",
//     viewDocument: "View document",
//     toSign: "To sign",
//     note: "Your electronic signature has the same legal value as a handwritten signature.",
//     cta: "SIGN DOCUMENTS",
//     poweredBy: "Powered by",
//   },
//
//   arrival: {
//     documentTitle: "Ready for your arrival | Europea",
//     title: "Ready for your arrival!",
//     subtitle: "Great! Your contract has been signed successfully.",
//     body: "We look forward to welcoming you. When you arrive, you can complete the next steps in just a few minutes.",
//     upcomingInventory: "Property condition & inventory",
//     upcomingInsurance: "Insurance certificate",
//     upcomingPractical: "Practical information",
//     cta: "CONTINUE AFTER ARRIVAL",
//   },
//
//   inventory: {
//     documentTitle: "Property review & inventory | Europea",
//     title: "Property review & inventory",
//     subtitle: "Please check the condition of the apartment upon your arrival.",
//     cleaningPhotos: "Cleaning photos",
//     cleaningPhotosCaption: "Photos taken after cleaning",
//     viewAllPhotos: "View all photos",
//     yourCheck: "Your check",
//     yourCheckCaption:
//       "Please verify the general condition and share your comments.",
//     commentsLabel: "Your comments",
//     commentsPlaceholder: "Add your comments...",
//     photosUpTo: (max: number) => `Photos (up to ${max})`,
//     addPhotos: "Add photos",
//     limitReached: (max: number) => `Limit of ${max} reached`,
//     cta: "CONFIRM & SIGN INVENTORY",
//   },
//
//   insurance: {
//     documentTitle: "Protect your stay | Europea",
//     title: "Protect your stay",
//     subtitle: "Please upload your liability insurance certificate.",
//     requiredTitle: "Required",
//     requiredCaption: "For the safety of your stay",
//     coversTitle: "Covers damages",
//     coversCaption: "Civil liability for you and the property",
//     secureTitle: "Secure",
//     secureCaption: "Used only for your stay",
//     certificate: "Insurance certificate",
//     formats: (maxMb: number) =>
//       `Accepted formats: PDF, JPG or PNG – Max. ${maxMb} MB`,
//     promptLine1: "Drag & drop your file",
//     promptLine2: "or click to upload",
//     cta: "I’VE UPLOADED MY INSURANCE",
//     footnote: "Your data is safe & confidential",
//   },
//
//   home: {
//     documentTitle: "Welcome home | Europea",
//     title: "Welcome home!",
//     subtitle: "All steps are complete. We wish you a wonderful stay!",
//     summary: "Summary",
//     identityVerified: "Identity verified",
//     contractSigned: "Contract signed",
//     inventoryConfirmed: "Inventory confirmed",
//     insuranceReceived: "Insurance received",
//     documentsEmailed: "All your documents have been sent by email.",
//     needHelp: "Need help during your stay?",
//   },
//
//   dropzone: {
//     changeFile: "Tap to choose a different file",
//     wrongType: "Please upload a PDF, JPG or PNG file.",
//     tooLarge: (maxMb: number) => `Your file is larger than ${maxMb} MB.`,
//   },
// };
//
// /** The English dictionary is the shape; French must satisfy it exactly. */
// export type Dictionary = typeof en;
//
// const fr: Dictionary = {
//   common: {
//     back: "Retour",
//     language: "Langue",
//     stepOf: (step: number, total: number) => `Étape ${step} sur ${total}`,
//   },
//
//   welcome: {
//     documentTitle: "Bienvenue chez vous | Europea",
//     titleLine1: "Bienvenue",
//     titleLine2: "chez vous",
//     subtitle:
//       "Nous sommes ravis de vous accueillir. Complétez votre onboarding en quelques étapes simples.",
//     // Not on the French board — its step 1 has no booking row.
//     bookingVia: "Votre réservation via",
//     apartment: "Appartement",
//     checkIn: "Arrivée",
//     checkOut: "Départ",
//     cta: "DÉMARRER MON ONBOARDING",
//   },
//
//   verify: {
//     documentTitle: "Vérifiez votre identité | Europea",
//     title: "Vérifiez votre identité",
//     subtitle: "Cette étape est obligatoire pour finaliser votre contrat.",
//     notice:
//       "Vos documents sont traités de manière sécurisée et supprimés automatiquement après vérification (sous 7 jours).",
//     promptLine1: "Glissez-déposez votre pièce d’identité",
//     promptLine2: "ou cliquez pour parcourir",
//     hintLine1: "Passeport ou Carte d’identité en cours de validité",
//     hintLine2: (maxMb: number) => `(PDF, JPG ou PNG – Max. ${maxMb} Mo)`,
//     // The board reads "MON OBOUMENT" — a typo in the artwork for "MON DOCUMENT".
//     cta: "J’AI TÉLÉVERSÉ MON DOCUMENT",
//     footnote: "Données 100% sécurisées & confidentielles",
//   },
//
//   contract: {
//     documentTitle: "Examinez et signez votre contrat | Europea",
//     title: "Examinez et signez votre contrat",
//     subtitle: "Veuillez lire attentivement les documents ci-dessous.",
//     mainContractTitle: "Contrat de location meublée",
//     mainContractCaption: "Bail mobilité",
//     attestationTitle: "Attestation de séjour (optionnelle)",
//     attestationCaption: "Pour bail mobilité",
//     viewDocument: "Voir le document",
//     toSign: "À signer",
//     note: "Votre signature électronique a la même valeur légale qu’une signature manuscrite.",
//     cta: "SIGNER LES DOCUMENTS",
//     poweredBy: "Powered by",
//   },
//
//   arrival: {
//     documentTitle: "Prêt pour votre arrivée | Europea",
//     title: "Prêt pour votre arrivée !",
//     subtitle: "Merci ! Votre contrat a été signé avec succès.",
//     body: "Nous avons hâte de vous accueillir. Après votre arrivée, vous pourrez compléter les prochaines étapes en quelques minutes.",
//     upcomingInventory: "État des lieux d’entrée",
//     upcomingInsurance: "Upload d’assurance",
//     upcomingPractical: "Informations pratiques",
//     cta: "CONTINUER APRÈS MON ARRIVÉE",
//   },
//
//   inventory: {
//     documentTitle: "État des lieux & inventaire | Europea",
//     title: "État des lieux & inventaire",
//     subtitle: "Merci de vérifier l’état de l’appartement à votre arrivée.",
//     cleaningPhotos: "Photos de propreté",
//     cleaningPhotosCaption: "Voici les photos prises après le ménage.",
//     viewAllPhotos: "Voir toutes les photos",
//     yourCheck: "À votre tour",
//     yourCheckCaption:
//       "Merci de vérifier l’état général et de nous signaler toute anomalie.",
//     commentsLabel: "Vos commentaires",
//     commentsPlaceholder: "Ajoutez vos commentaires...",
//     photosUpTo: (max: number) => `Photos (jusqu’à ${max})`,
//     addPhotos: "Ajouter des photos",
//     limitReached: (max: number) => `Limite de ${max} atteinte`,
//     cta: "CONFIRMER ET SIGNER L’ÉTAT DES LIEUX",
//   },
//
//   insurance: {
//     documentTitle: "Protégez votre séjour | Europea",
//     title: "Protégez votre séjour",
//     subtitle: "Merci de nous transmettre votre attestation d’assurance.",
//     requiredTitle: "Obligatoire",
//     requiredCaption: "Pour toute la durée de votre séjour",
//     coversTitle: "Couvre les dommages",
//     coversCaption: "Responsabilité civile et dommages locatifs",
//     secureTitle: "Sécurisé",
//     secureCaption: "Utilisation uniquement pour votre séjour",
//     certificate: "Attestation d’assurance",
//     formats: (maxMb: number) =>
//       `Formats acceptés : PDF, JPG ou PNG – Max. ${maxMb} Mo`,
//     promptLine1: "Glissez-déposez votre document",
//     promptLine2: "ou cliquez pour parcourir",
//     cta: "J’AI TÉLÉVERSÉ MON ATTESTATION",
//     footnote: "Vos données sont confidentielles et sécurisées",
//   },
//
//   home: {
//     documentTitle: "Bienvenue chez vous | Europea",
//     title: "Bienvenue chez vous !",
//     subtitle:
//       "Toutes les étapes sont complétées. Nous vous souhaitons un excellent séjour !",
//     summary: "Récapitulatif",
//     identityVerified: "Identité vérifiée",
//     contractSigned: "Contrat signé",
//     inventoryConfirmed: "État des lieux confirmé",
//     insuranceReceived: "Assurance reçue",
//     documentsEmailed: "Tous vos documents vous ont été envoyés par email.",
//     needHelp: "Besoin d’aide pendant votre séjour ?",
//   },
//
//   dropzone: {
//     // Not on the board: no error or post-upload state is drawn.
//     changeFile: "Touchez pour choisir un autre fichier",
//     wrongType: "Veuillez importer un fichier PDF, JPG ou PNG.",
//     tooLarge: (maxMb: number) => `Votre fichier dépasse ${maxMb} Mo.`,
//   },
// };
//
// export const STRINGS = { en, fr };

// ==== ACTIVE IMPLEMENTATION ==================================================

/**
 * Copy for the tenant onboarding steps.
 *
 * The French is lifted from the French mockup board in the technical
 * specification, which is the client's own wording — prefer it over a fresh
 * translation. Strings with no counterpart on the board (validation errors,
 * screen-reader labels, page titles) are translated here and marked.
 *
 * French typography: a no-break space precedes `! ? :` so the punctuation
 * cannot be orphaned onto the next line.
 */

const en = {
  common: {
    back: "Go back",
    language: "Language",
    /** Screen-reader only; the visible indicator is "2 / 7". */
    stepOf: (step: number, total: number) => `Step ${step} of ${total}`,
  },

  welcome: {
    documentTitle: "Welcome home | Europea",
    titleLine1: "Welcome",
    titleLine2: "home",
    subtitle:
      "We’re delighted to have you. Complete your onboarding in a few simple steps.",
    bookingVia: "Your booking via",
    apartment: "Apartment",
    checkIn: "Check-in",
    checkOut: "Check-out",
    cta: "START MY ONBOARDING",
  },

  verify: {
    documentTitle: "Verify your identity | Europea",
    title: "Verify your identity",
    subtitle: "This step is required to finalize your rental agreement.",
    notice:
      "Your documents are encrypted and securely deleted automatically after verification (within 7 days).",
    promptLine1: "Drag & drop your ID document",
    promptLine2: "or click to upload",
    hintLine1: "Accepted documents: Passport or ID card",
    hintLine2: (maxMb: number) => `(PDF, JPG or PNG – Max. ${maxMb} MB)`,
    cta: "I’VE UPLOADED MY DOCUMENT",
    footnote: "100% secure & confidential",
  },

  contract: {
    documentTitle: "Review & sign your contract | Europea",
    title: "Review & sign your contract",
    subtitle: "Please review the documents below carefully.",
    mainContractTitle: "Furnished rental agreement",
    mainContractCaption: "Main contract",
    attestationTitle: "Stay attestation (optional)",
    attestationCaption: "For mobility lease",
    viewDocument: "View document",
    toSign: "To sign",
    note: "Your electronic signature has the same legal value as a handwritten signature.",
    cta: "SIGN DOCUMENTS",
    poweredBy: "Powered by",
  },

  arrival: {
    documentTitle: "Ready for your arrival | Europea",
    title: "Ready for your arrival!",
    subtitle: "Great! Your contract has been signed successfully.",
    body: "We look forward to welcoming you. When you arrive, you can complete the next steps in just a few minutes.",
    upcomingInventory: "Property condition & inventory",
    upcomingInsurance: "Insurance certificate",
    upcomingPractical: "Practical information",
    cta: "CONTINUE AFTER ARRIVAL",
  },

  inventory: {
    documentTitle: "Property review & inventory | Europea",
    title: "Property review & inventory",
    subtitle: "Please check the condition of the apartment upon your arrival.",
    cleaningPhotos: "Cleaning photos",
    cleaningPhotosCaption: "Photos taken after cleaning",
    viewAllPhotos: "View all photos",
    yourCheck: "Your check",
    yourCheckCaption:
      "Please verify the general condition and share your comments.",
    commentsLabel: "Your comments",
    commentsPlaceholder: "Add your comments...",
    photosUpTo: (max: number) => `Photos (up to ${max})`,
    addPhotos: "Add photos",
    limitReached: (max: number) => `Limit of ${max} reached`,
    cta: "CONFIRM & SIGN INVENTORY",
  },

  insurance: {
    documentTitle: "Protect your stay | Europea",
    title: "Protect your stay",
    subtitle: "Please upload your liability insurance certificate.",
    requiredTitle: "Required",
    requiredCaption: "For the safety of your stay",
    coversTitle: "Covers damages",
    coversCaption: "Civil liability for you and the property",
    secureTitle: "Secure",
    secureCaption: "Used only for your stay",
    certificate: "Insurance certificate",
    formats: (maxMb: number) =>
      `Accepted formats: PDF, JPG or PNG – Max. ${maxMb} MB`,
    promptLine1: "Drag & drop your file",
    promptLine2: "or click to upload",
    cta: "I’VE UPLOADED MY INSURANCE",
    footnote: "Your data is safe & confidential",
  },

  home: {
    documentTitle: "Welcome home | Europea",
    title: "Welcome home!",
    subtitle: "All steps are complete. We wish you a wonderful stay!",
    summary: "Summary",
    identityVerified: "Identity verified",
    contractSigned: "Contract signed",
    inventoryConfirmed: "Inventory confirmed",
    insuranceReceived: "Insurance received",
    documentsEmailed: "All your documents have been sent by email.",
    needHelp: "Need help during your stay?",
  },

  dropzone: {
    changeFile: "Tap to choose a different file",
    wrongType: "Please upload a PDF, JPG or PNG file.",
    /** The file was valid but could not be sent. */
    uploadFailed:
      "Your certificate could not be sent. Please check your connection and try again.",
    tooLarge: (maxMb: number) => `Your file is larger than ${maxMb} MB.`,
  },

  /**
   * The desktop onboarding workspace (/tenant): a fixed step rail, a centre
   * column that swaps per step, and the stay summary. Its own namespace because
   * the copy is written for a wide three-column canvas — the phone steps above
   * keep their shorter wording.
   */
  desktop: {
    documentTitle: "Your onboarding | Europea",
    needHelp: "Need help?",
    helpTitle: "How can we help?",
    helpBody:
      "Our reservations team is here to help with your onboarding, documents or upcoming stay.",
    helpReplyTime: "We usually reply within one business day.",
    helpEmailCta: "Email our team",
    helpReference: "Please include your booking reference:",
    helpClose: "Close",
    back: "Back",
    stepOf: (step: number, total: number) => `Step ${step} of ${total}`,

    eyebrow: "Your onboarding",
    /** The compact progress line shown in place of the rail on a phone. */
    stepCounter: (n: number, total: number) => `Step ${n} of ${total}`,
    // The greeting is set as two lines, the name on its own:
    // greeting: (firstName: string) => `Welcome home, ${firstName}.`,
    greeting1: "Welcome home,",
    greeting2: (firstName: string) => `${firstName}.`,
    intro: "Complete your onboarding to get ready for your stay. Your progress is saved automatically.",
    afterArrival: "After arrival",
    currentStep: (minutes: number) => `Current step · ${minutes} minutes`,
    privateTitle: "Private and secure",
    privateBody:
      "Your documents are encrypted and automatically deleted after verification.",

    stay: {
      eyebrow: "Your stay",
      checkIn: "Check-in",
      checkOut: "Check-out",
      tenantName: "Tenant name",
      bookingReference: "Booking reference",
      bookedVia: "Booked via",
      fineprint: "Secure verification · GDPR compliant",
      copyright: (year: number) => `© ${year} Europea Residences`,
    },

    /**
     * Rail copy only. Each step reads three ways depending on where the tenant
     * is: `caption` before they reach it, `currentCaption` while they are on
     * it, `doneCaption` once the work behind it is finished. The heading copy
     * that used to live here moved to the panel blocks below, because the
     * result screens set their own and needed shapes this record cannot hold.
     */
    steps: {
      welcome: {
        rail: "Welcome",
        caption: "Stay details confirmed",
        currentCaption: "Stay details confirmed",
        doneCaption: "Stay details confirmed",
        // Moved to welcomePanel:
        // title: "Your stay is confirmed",
        // subtitle: "Here is everything we need before you arrive. It takes about ten minutes in total.",
        // cta: "Start onboarding",
      },
      identity: {
        rail: "Verify your identity",
        caption: "One valid document",
        currentCaption: "Current step · 2 minutes",
        doneCaption: "Identity document added",
        // Moved to identityPanel:
        // title: "Verify your identity",
        // subtitle: "We need one valid identity document to finalise your rental agreement.",
        // cta: "Review documents",
      },
      contract: {
        rail: "Contract & Annexes",
        caption: "One signature",
        currentCaption: "Current step · 7 minutes",
        doneCaption: "Both documents signed",
        // Moved to contractPanel:
        // title: "Review & sign your documents",
        // subtitle: "Review your rental contract and eligibility attestation. One signature will be applied to both documents.",
        // cta: "View final screen",
      },
      arrival: {
        rail: "Ready for your arrival",
        caption: "Flow 1 complete",
        currentCaption: "Flow 1 complete",
        doneCaption: "Flow 1 complete",
        // Moved to arrivalPanel:
        // title: "Ready for your arrival",
        // subtitle: "Your contract is signed. The remaining steps unlock once you have checked in.",
        // cta: "Continue after arrival",
      },
      inventory: {
        rail: "Review inventory",
        caption: "Available after check-in",
        currentCaption: "Current step · 5 minutes",
        doneCaption: "Inventory signed",
        // Moved to inventoryPanel:
        // title: "Property review & inventory",
        // subtitle: "Check the condition of the apartment and tell us about anything that needs attention.",
        // cta: "Confirm & sign inventory",
      },
      insurance: {
        rail: "Home insurance",
        caption: "Upload certificate",
        /** Not a duration: this step is the one thing with a deadline. */
        currentCaption: "Due within 10 days",
        doneCaption: "Certificate received",
        // Moved to insurancePanel:
        // title: "Protect your stay",
        // subtitle: "Upload your liability insurance certificate for the duration of your stay.",
        // cta: "Submit certificate",
      },
      home: {
        rail: "Welcome home",
        caption: "All steps complete",
        currentCaption: "All steps complete",
        doneCaption: "All steps complete",
        // Moved to homePanel:
        // title: "Welcome home",
        // subtitle: "Every step is complete. We wish you a wonderful stay in Paris.",
        // cta: "",
      },
    },

    /**
     * Not reachable in the reference — its Welcome step is always complete and
     * its rail button disabled, so the journey opens on identity. Kept for the
     * flow that starts a tenant from the beginning.
     */
    welcomePanel: {
      eyebrow: "STEP 1 OF 4",
      title: "Your stay is secured",
      subtitle:
        "To comply with local regulations, please complete the steps below. It takes about 10 minutes in total.",
      cta: "Start onboarding",
      heading: "What we need before you arrive",
      afterArrivalNote:
        "The inventory, insurance and welcome steps open automatically once you have checked in.",
      minutes: (m: number) => `${m} min`,
    },

    contractPanel: {
      eyebrow: "STEP 3 OF 4",
      title: "Review & sign your documents",
      /** The attestation is what makes it plural; without one it is a contract. */
      titleContractOnly: "Review & sign your rental contract",
      subtitle:
        "Review your rental contract and eligibility attestation. One signature will be applied to both documents.",
      cta: "Finalise your stay",

      /** The scenario switch is a review affordance, not part of the journey. */
      dmsLabel: "DMS scenario preview",
      dmsCaption: "This control is not visible to the tenant.",
      scenarioBoth: "Contract + attestation",
      scenarioContractOnly: "Contract only",

      property: "Property",
      rentalPeriod: "Rental period",

      openDocument: "Open document",
      /** One file holds the lease and any annexes, so the action is singular. */
      openDocuments: "Open the documents",
      openDocumentsSingle: "Open the document",
      preview: "Preview",
      contractTitle: "Residential rental agreement",
      contractCaption: "12 pages · English version",
      /** Real length and language, replacing the fixed "12 pages" strings. */
      documentCaption: (pages: number, language: string) =>
        `${pages} ${pages === 1 ? "page" : "pages"} · ${language}`,
      languageNames: { english: "English", french: "French", dutch: "Dutch" } as Record<string, string>,
      attestationTitle: "Eligibility attestation",
      attestationCaption: "2 pages · Declaration on honour",


      reasonTitle: "Reason for your temporary stay",
      reasonCaption: "This selection will appear in your attestation",
      reasonProfessional: "Professional assignment",
      reasonProfessionalCaption:
        "Temporary work, transfer or professional mission",
      reasonStudies: "Studies or training",
      reasonStudiesCaption:
        "Higher education, internship or vocational training",

      confirmBoth:
        "I confirm that I have reviewed both documents and certify that the information in my eligibility attestation is accurate.",
      // Superseded by the reference wording:
      // confirmContractOnly: "I confirm that I have reviewed the rental contract and that its details are accurate.",
      confirmContractOnly:
        "I confirm that I have reviewed and accepted the rental contract.",

      signatureEyebrow: "Electronic signature",
      signatureTitle: "Add your handwritten signature",
      signatureCaption: "Use your finger, stylus or mouse on the next screen.",
      signBoth: "Sign both documents",
      // Superseded by the reference wording:
      // signContractOnly: "Sign the contract",
      signContractOnly: "Sign contract",
      // Count-based label — singular for one document, plural for several.
      signDocument: "Sign document",
      signDocuments: "Sign documents",
      /** Once signed, the card turns green and offers a redo. */
      signatureDone: "Signature completed",
      signedBy: (name: string, date: string) => `Signed by ${name} · ${date}`,
      signAgain: "Sign again",
      fineprintBoth:
        "One secure electronic signature will be placed on the rental contract and the eligibility attestation.",
      // Superseded by the reference wording:
      // fineprintContractOnly: "One secure electronic signature will be placed on the rental contract.",
      fineprintContractOnly:
        "Your electronic signature will be placed on the rental contract.",

      /** The design names the previous step rather than saying "Back". */
      backTo: "Identity",
      // Superseded by the reference wording:
      // subtitleContractOnly: "Review your rental contract. One signature will be applied to it.",
      subtitleContractOnly:
        "Review your rental contract before adding your electronic signature.",
    },

    identityPanel: {
      eyebrow: "STEP 2 OF 4",
      title: "Verify your identity",
      subtitle:
        "We need one valid identity document to finalise your rental agreement.",
      cta: "Review documents",
      acceptedTitle: "Accepted documents",
      acceptedBody: "Passport, national identity card or residence permit",
      dropTitle: "Drop your document here",
      /** Replaces the prompt once a file passes validation. */
      readyTitle: "Document ready",
      browsePrefix: "or ",
      browse: "browse your files",
      formats: (maxMb: number) => `PDF, JPG or PNG · Maximum ${maxMb} MB`,
      safetyLabel: "Your document safety matters:",
      safetyBody:
        "Your identity document is printed for your rental file. Every digital copy is permanently deleted from all our servers within 7 days.",
    },

    arrivalPanel: {
      eyebrow: "Onboarding complete",
      title: "Everything is ready.",
      subtitle: (firstName: string, property: string, date: string) =>
        `Thank you, ${firstName}. We look forward to welcoming you to ${property} on ${date}.`,
      identityDone: "Identity verified",
      identityDoneCaption: "Your document has been received",
      contractDone: "Rental contract signed",
      contractDoneCaption: "A copy is ready to download",
      attestationDone: "Attestation signed",
      attestationDoneCaption: "Your declaration is attached to the contract",
      checkInFrom: "Check-in from 3:00 PM",
      calloutTag: "After check-in",
      calloutTitle: "Two final steps will become available",
      calloutBody:
        "Review the apartment inventory and provide your home-insurance certificate.",
      download: "Download signed documents",
      cta: "Preview Flow 2",
      restart: "Restart design preview",
    },

    inventoryPanel: {
      eyebrow: "Step 5 of 7 · After check-in",
      title: "Review your apartment inventory",
      subtitle:
        "Review the photos taken by our team after cleaning, add any useful photos of your own, then sign the inventory.",
      noticeTitle:
        "A simplified record of the apartment’s overall condition",
      noticeBody:
        "This reduced inventory is intended to show the general state of the apartment at move-in. It is not an exhaustive list of every minor mark, sign of wear or imperfection.",
      galleryTitle: "Europea inspection photos",
      galleryCaption: (date: string) => `Taken on ${date} after cleaning`,
      awaitingPhotos:
        "Our team has not added the inspection photos yet. You will be able to sign the inventory as soon as they are available.",
      awaitingCheckIn: (date: string) =>
        `The inventory records the apartment's condition at move-in, so it can only be signed once your stay has begun on ${date}.`,
      photoCount: (n: number) => `${n} photos`,
      morePhotos: (n: number) => `+${n} photos`,
      yourPhotos: "Your additional photos",
      yourPhotosCaption: (max: number) =>
        `Optional · Add up to ${max} photos taken at move-in`,
      addPhotos: "Add photos",
      photosReady: (n: number) =>
        `${n} tenant photo${n > 1 ? "s" : ""} ready to attach`,
      photosSubmitted: (n: number) =>
        `${n} photo${n > 1 ? "s" : ""} submitted with your inventory`,
      remove: "Remove",
      /**
       * With no inspection photos on record there is nothing for the tenant to
       * have reviewed, and asking them to confirm they reviewed zero of them
       * is a declaration that means nothing — worse, it is one they may later
       * be held to. They confirm what they can actually attest: the apartment
       * as they found it.
       */
      confirm: (n: number) =>
        n === 0
          ? `I confirm that this inventory reflects the condition of the apartment as I found it at move-in, and understand that it records the apartment’s overall condition rather than every minor imperfection.`
          : `I confirm that I have reviewed the ${n} inspection photo${n === 1 ? "" : "s"} and understand that this simplified inventory records the apartment’s overall condition rather than every minor imperfection.`,
      signCta: "Sign inventory",
      signatureDone: "Inventory signature completed",
      back: "Arrival summary",
      cta: "Continue to insurance",
      galleryClose: "Close gallery",
      galleryPrev: "Previous photo",
      galleryNext: "Next photo",
    },

    insurancePanel: {
      eyebrow: "Step 6 of 7 · Within 10 days",
      title: "Provide your home-insurance certificate",
      subtitle:
        "Upload proof of home insurance covering the apartment for the full duration of your stay.",
      dueLabel: "Document due date",
      /** Replaces the neutral label once the due date has passed. */
      overdueLabel: "Overdue",
      overdueBody: (days: number) =>
        `This was due ${days} ${days === 1 ? "day" : "days"} ago. Please send it as soon as you can — you can still upload it.`,
      dueTitle: "Within 10 days after moving in",
      dueBody: (date: string) => `Your check-in date is ${date}.`,
      requirementsTitle: "Your certificate should include",
      requirementName: "Your full name",
      requirementAddress: "The rental property address",
      requirementDates: "Coverage dates for your stay",
      requirementLiability: "Tenant liability or equivalent coverage",
      uploadTitle: "Upload your insurance certificate",
      uploadReady: "Document ready",
      browse: "Browse files",
      replace: "Replace",
      note: "Your certificate is stored securely with your rental file and is accessible only to the property-management team.",
      back: "Inventory",
      cta: "Finish onboarding",
      // Superseded: the reference uses a compact upload row, not a dropzone.
      // dropTitle: "Drop your certificate here",
    },

    homePanel: {
      eyebrow: "Welcome home",
      title: "Everything is ready.",
      subtitle: (firstName: string, property: string, date: string) =>
        `Thank you, ${firstName}. We look forward to welcoming you to ${property} on ${date}.`,
      documentsDone: "Identity and rental documents",
      documentsDoneCaption: "Completed before arrival",
      inventoryDone: "Apartment inventory",
      inventoryDoneCaption: (n: number) =>
        `${n} Europea photos reviewed and signed`,
      insuranceDone: "Home-insurance certificate",
      insuranceDoneCaption: "Certificate received",
      download: "Download my documents",
      /** Named when a document could not be produced, so nothing goes missing silently. */
      missingContract: "your rental contract",
      missingInventory: "your inventory report",
      downloadIncomplete: (missing: string) =>
        `We could not prepare ${missing}. Everything else has downloaded — please try again, and contact us if it keeps failing.`,
      cta: "Go to my tenant space",
      restart: "Review Flow 2 again",
    },

    signaturePanel: {
      eyebrow: "Secure digital signature",
      title: "Add your signature",
      subtitlePrefix:
        "Sign inside the box using your finger, stylus or mouse. Your signature will be applied to: ",
      instructionTitle: "Sign naturally inside the box",
      instructionBody:
        "For the best result, turn your phone horizontally or use a stylus where available.",
      placeholder: "Sign here",
      clear: "Clear",
      tenantName: "Tenant name",
      dateOfSignature: "Date of signature",
      consent: (plural: boolean) =>
        `I confirm that this handwritten electronic signature is mine and that I intend to sign the document${
          plural ? "s" : ""
        } listed above.`,
      confirm: "Confirm signature",
      /** Shown on the confirm button while the signature is being saved. */
      confirming: "Saving your signature…",
      /** The save failed; the tenant has not signed anything yet. */
      saveFailed:
        "Your signature could not be saved. Please check your connection and confirm again.",
      backToDocuments: "Back to documents",
      backToInventory: "Back to inventory",
      /** What the signature is being applied to, named in the subtitle. */
      labelBoth: "Rental contract and eligibility attestation",
      labelContract: "Rental contract",
      labelInventory: "Apartment inventory",
    },
  },
};

/** The English dictionary is the shape; French must satisfy it exactly. */
export type Dictionary = typeof en;

const fr: Dictionary = {
  common: {
    back: "Retour",
    language: "Langue",
    stepOf: (step: number, total: number) => `Étape ${step} sur ${total}`,
  },

  welcome: {
    documentTitle: "Bienvenue chez vous | Europea",
    titleLine1: "Bienvenue",
    titleLine2: "chez vous",
    subtitle:
      "Nous sommes ravis de vous accueillir. Complétez votre onboarding en quelques étapes simples.",
    // Not on the French board — its step 1 has no booking row.
    bookingVia: "Votre réservation via",
    apartment: "Appartement",
    checkIn: "Arrivée",
    checkOut: "Départ",
    cta: "DÉMARRER MON ONBOARDING",
  },

  verify: {
    documentTitle: "Vérifiez votre identité | Europea",
    title: "Vérifiez votre identité",
    subtitle: "Cette étape est obligatoire pour finaliser votre contrat.",
    notice:
      "Vos documents sont traités de manière sécurisée et supprimés automatiquement après vérification (sous 7 jours).",
    promptLine1: "Glissez-déposez votre pièce d’identité",
    promptLine2: "ou cliquez pour parcourir",
    hintLine1: "Passeport ou Carte d’identité en cours de validité",
    hintLine2: (maxMb: number) => `(PDF, JPG ou PNG – Max. ${maxMb} Mo)`,
    // The board reads "MON OBOUMENT" — a typo in the artwork for "MON DOCUMENT".
    cta: "J’AI TÉLÉVERSÉ MON DOCUMENT",
    footnote: "Données 100% sécurisées & confidentielles",
  },

  contract: {
    documentTitle: "Examinez et signez votre contrat | Europea",
    title: "Examinez et signez votre contrat",
    subtitle: "Veuillez lire attentivement les documents ci-dessous.",
    mainContractTitle: "Contrat de location meublée",
    mainContractCaption: "Bail mobilité",
    attestationTitle: "Attestation de séjour (optionnelle)",
    attestationCaption: "Pour bail mobilité",
    viewDocument: "Voir le document",
    toSign: "À signer",
    note: "Votre signature électronique a la même valeur légale qu’une signature manuscrite.",
    cta: "SIGNER LES DOCUMENTS",
    poweredBy: "Powered by",
  },

  arrival: {
    documentTitle: "Prêt pour votre arrivée | Europea",
    title: "Prêt pour votre arrivée !",
    subtitle: "Merci ! Votre contrat a été signé avec succès.",
    body: "Nous avons hâte de vous accueillir. Après votre arrivée, vous pourrez compléter les prochaines étapes en quelques minutes.",
    upcomingInventory: "État des lieux d’entrée",
    upcomingInsurance: "Upload d’assurance",
    upcomingPractical: "Informations pratiques",
    cta: "CONTINUER APRÈS MON ARRIVÉE",
  },

  inventory: {
    documentTitle: "État des lieux & inventaire | Europea",
    title: "État des lieux & inventaire",
    subtitle: "Merci de vérifier l’état de l’appartement à votre arrivée.",
    cleaningPhotos: "Photos de propreté",
    cleaningPhotosCaption: "Voici les photos prises après le ménage.",
    viewAllPhotos: "Voir toutes les photos",
    yourCheck: "À votre tour",
    yourCheckCaption:
      "Merci de vérifier l’état général et de nous signaler toute anomalie.",
    commentsLabel: "Vos commentaires",
    commentsPlaceholder: "Ajoutez vos commentaires...",
    photosUpTo: (max: number) => `Photos (jusqu’à ${max})`,
    addPhotos: "Ajouter des photos",
    limitReached: (max: number) => `Limite de ${max} atteinte`,
    cta: "CONFIRMER ET SIGNER L’ÉTAT DES LIEUX",
  },

  insurance: {
    documentTitle: "Protégez votre séjour | Europea",
    title: "Protégez votre séjour",
    subtitle: "Merci de nous transmettre votre attestation d’assurance.",
    requiredTitle: "Obligatoire",
    requiredCaption: "Pour toute la durée de votre séjour",
    coversTitle: "Couvre les dommages",
    coversCaption: "Responsabilité civile et dommages locatifs",
    secureTitle: "Sécurisé",
    secureCaption: "Utilisation uniquement pour votre séjour",
    certificate: "Attestation d’assurance",
    formats: (maxMb: number) =>
      `Formats acceptés : PDF, JPG ou PNG – Max. ${maxMb} Mo`,
    promptLine1: "Glissez-déposez votre document",
    promptLine2: "ou cliquez pour parcourir",
    cta: "J’AI TÉLÉVERSÉ MON ATTESTATION",
    footnote: "Vos données sont confidentielles et sécurisées",
  },

  home: {
    documentTitle: "Bienvenue chez vous | Europea",
    title: "Bienvenue chez vous !",
    subtitle:
      "Toutes les étapes sont complétées. Nous vous souhaitons un excellent séjour !",
    summary: "Récapitulatif",
    identityVerified: "Identité vérifiée",
    contractSigned: "Contrat signé",
    inventoryConfirmed: "État des lieux confirmé",
    insuranceReceived: "Assurance reçue",
    documentsEmailed: "Tous vos documents vous ont été envoyés par email.",
    needHelp: "Besoin d’aide pendant votre séjour ?",
  },

  dropzone: {
    // Not on the board: no error or post-upload state is drawn.
    changeFile: "Touchez pour choisir un autre fichier",
    wrongType: "Veuillez importer un fichier PDF, JPG ou PNG.",
    uploadFailed:
      "Votre attestation n’a pas pu être envoyée. Vérifiez votre connexion et réessayez.",
    tooLarge: (maxMb: number) => `Votre fichier dépasse ${maxMb} Mo.`,
  },

  // Not on the French board — the desktop workspace was specified in English
  // only, so this is translated here rather than lifted.
  desktop: {
    documentTitle: "Votre onboarding | Europea",
    needHelp: "Besoin d’aide ?",
    helpTitle: "Comment pouvons-nous vous aider ?",
    helpBody:
      "Notre équipe réservations est là pour vous aider avec votre onboarding, vos documents ou votre séjour à venir.",
    helpReplyTime: "Nous répondons généralement sous un jour ouvré.",
    helpEmailCta: "Écrire à notre équipe",
    helpReference: "Merci d’indiquer votre référence de réservation :",
    helpClose: "Fermer",
    back: "Retour",
    stepOf: (step: number, total: number) => `Étape ${step} sur ${total}`,

    eyebrow: "Votre onboarding",
    stepCounter: (n: number, total: number) => `Étape ${n} sur ${total}`,
    // greeting: (firstName: string) => `Bienvenue chez vous, ${firstName}.`,
    greeting1: "Bienvenue chez vous,",
    greeting2: (firstName: string) => `${firstName}.`,
    intro: "Finalisez votre dossier pour préparer votre séjour. Votre progression est enregistrée automatiquement.",
    afterArrival: "Après l’arrivée",
    currentStep: (minutes: number) => `Étape en cours · ${minutes} minutes`,
    privateTitle: "Confidentiel et sécurisé",
    privateBody:
      "Vos documents sont chiffrés et supprimés automatiquement après vérification.",

    stay: {
      eyebrow: "Votre séjour",
      checkIn: "Arrivée",
      checkOut: "Départ",
      tenantName: "Locataire",
      bookingReference: "Référence de réservation",
      bookedVia: "Réservé via",
      fineprint: "Vérification sécurisée · Conforme RGPD",
      copyright: (year: number) => `© ${year} Europea Residences`,
    },

    steps: {
      welcome: {
        rail: "Bienvenue",
        caption: "Séjour confirmé",
        currentCaption: "Séjour confirmé",
        doneCaption: "Séjour confirmé",
        // Moved to welcomePanel:
        // title: "Votre séjour est confirmé",
        // subtitle: "Voici tout ce dont nous avons besoin avant votre arrivée. Comptez une dizaine de minutes au total.",
        // cta: "Démarrer mon onboarding",
      },
      identity: {
        rail: "Vérifiez votre identité",
        caption: "Une pièce en cours de validité",
        currentCaption: "Étape en cours · 2 minutes",
        doneCaption: "Pièce d’identité ajoutée",
        // Moved to identityPanel:
        // title: "Vérifiez votre identité",
        // subtitle: "Une pièce d’identité en cours de validité suffit pour finaliser votre contrat de location.",
        // cta: "Vérifier les documents",
      },
      contract: {
        rail: "Contrat & Annexes",
        caption: "Une seule signature",
        currentCaption: "Étape en cours · 7 minutes",
        doneCaption: "Les deux documents sont signés",
        // Moved to contractPanel:
        // title: "Examinez et signez vos documents",
        // subtitle: "Examinez votre contrat de location et votre attestation d’éligibilité. Une seule signature sera apposée sur les deux documents.",
        // cta: "Voir l’écran final",
      },
      arrival: {
        rail: "Prêt pour votre arrivée",
        caption: "Flux 1 terminé",
        currentCaption: "Flux 1 terminé",
        doneCaption: "Flux 1 terminé",
        // Moved to arrivalPanel:
        // title: "Prêt pour votre arrivée",
        // subtitle: "Votre contrat est signé. Les étapes suivantes s’ouvriront après votre arrivée.",
        // cta: "Continuer après mon arrivée",
      },
      inventory: {
        rail: "État des lieux",
        caption: "Disponible après l’arrivée",
        currentCaption: "Étape en cours · 5 minutes",
        doneCaption: "État des lieux signé",
        // Moved to inventoryPanel:
        // title: "État des lieux & inventaire",
        // subtitle: "Vérifiez l’état de l’appartement et signalez-nous tout ce qui mérite notre attention.",
        // cta: "Confirmer et signer l’état des lieux",
      },
      insurance: {
        rail: "Assurance habitation",
        caption: "Attestation à téléverser",
        currentCaption: "À fournir sous 10 jours",
        doneCaption: "Attestation reçue",
        // Moved to insurancePanel:
        // title: "Protégez votre séjour",
        // subtitle: "Téléversez votre attestation de responsabilité civile pour la durée de votre séjour.",
        // cta: "Envoyer l’attestation",
      },
      home: {
        rail: "Bienvenue chez vous",
        caption: "Toutes les étapes sont complétées",
        currentCaption: "Toutes les étapes sont complétées",
        doneCaption: "Toutes les étapes sont complétées",
        // Moved to homePanel:
        // title: "Bienvenue chez vous",
        // subtitle: "Toutes les étapes sont complétées. Nous vous souhaitons un excellent séjour à Paris.",
        // cta: "",
      },
    },

    welcomePanel: {
      eyebrow: "Étape 1 sur 4",
      title: "Votre séjour est enregistré",
      subtitle:
        "Afin de respecter la réglementation locale, veuillez suivre les étapes ci-dessous pour finaliser votre séjour. Cela ne vous prendra qu’une dizaine de minutes.",
      cta: "Démarrer mon onboarding",
      heading: "Ce dont nous avons besoin avant votre arrivée",
      afterArrivalNote:
        "L’état des lieux, l’assurance et l’étape finale s’ouvriront automatiquement après votre arrivée.",
      minutes: (m: number) => `${m} min`,
    },

    contractPanel: {
      eyebrow: "Étape 3 sur 4",
      title: "Examinez et signez vos documents",
      titleContractOnly: "Examinez et signez votre contrat de location",
      subtitle:
        "Examinez votre contrat de location et votre attestation d’éligibilité. Une seule signature sera apposée sur les deux documents.",
      cta: "Finaliser votre séjour",

      dmsLabel: "Aperçu scénario DMS",
      dmsCaption: "Ce réglage n’est pas visible par le locataire.",
      scenarioBoth: "Contrat + attestation",
      scenarioContractOnly: "Contrat seul",

      property: "Logement",
      rentalPeriod: "Période de location",

      openDocument: "Ouvrir le document",
      openDocuments: "Ouvrir les documents",
      openDocumentsSingle: "Ouvrir le document",
      preview: "Aperçu",
      contractTitle: "Contrat de location meublée",
      contractCaption: "12 pages · Version française",
      documentCaption: (pages: number, language: string) =>
        `${pages} ${pages === 1 ? "page" : "pages"} · ${language}`,
      languageNames: { english: "Anglais", french: "Français", dutch: "Néerlandais" } as Record<string, string>,
      attestationTitle: "Attestation d’éligibilité",
      attestationCaption: "2 pages · Déclaration sur l’honneur",


      reasonTitle: "Motif de votre séjour temporaire",
      reasonCaption: "Ce choix figurera sur votre attestation",
      reasonProfessional: "Mission professionnelle",
      reasonProfessionalCaption:
        "Travail temporaire, mutation ou mission professionnelle",
      reasonStudies: "Études ou formation",
      reasonStudiesCaption:
        "Enseignement supérieur, stage ou formation professionnelle",

      confirmBoth:
        "Je confirme avoir examiné les deux documents et certifie que les informations de mon attestation d’éligibilité sont exactes.",
      // Superseded by the reference wording:
      // confirmContractOnly: "Je confirme avoir examiné le contrat de location et que ses informations sont exactes.",
      confirmContractOnly:
        "Je confirme avoir examiné et accepté le contrat de location.",

      signatureEyebrow: "Signature électronique",
      signatureTitle: "Ajoutez votre signature manuscrite",
      signatureCaption:
        "Utilisez votre doigt, un stylet ou votre souris à l’écran suivant.",
      signBoth: "Signer les deux documents",
      signContractOnly: "Signer le contrat",
      signDocument: "Signer le document",
      signDocuments: "Signer les documents",
      signatureDone: "Signature effectuée",
      signedBy: (name: string, date: string) => `Signé par ${name} · ${date}`,
      signAgain: "Signer à nouveau",
      fineprintBoth:
        "Une seule signature électronique sécurisée sera apposée sur le contrat de location et l’attestation d’éligibilité.",
      // Superseded by the reference wording:
      // fineprintContractOnly: "Une seule signature électronique sécurisée sera apposée sur le contrat de location.",
      fineprintContractOnly:
        "Votre signature électronique sera apposée sur le contrat de location.",

      backTo: "Identité",
      // Superseded by the reference wording:
      // subtitleContractOnly: "Examinez votre contrat de location. Une seule signature y sera apposée.",
      subtitleContractOnly:
        "Examinez votre contrat de location avant d’ajouter votre signature électronique.",
    },

    identityPanel: {
      eyebrow: "Étape 2 sur 4",
      title: "Vérifiez votre identité",
      subtitle:
        "Une pièce d’identité en cours de validité suffit pour finaliser votre contrat de location.",
      cta: "Vérifier les documents",
      acceptedTitle: "Documents acceptés",
      acceptedBody: "Passeport, carte nationale d’identité ou titre de séjour",
      dropTitle: "Déposez votre document ici",
      readyTitle: "Document prêt",
      browsePrefix: "ou ",
      browse: "parcourez vos fichiers",
      formats: (maxMb: number) => `PDF, JPG ou PNG · Maximum ${maxMb} Mo`,
      safetyLabel: "La sécurité de vos documents compte :",
      safetyBody:
        "Votre pièce d’identité est imprimée pour votre dossier de location. Toute copie numérique est définitivement supprimée de nos serveurs sous 7 jours.",
    },

    arrivalPanel: {
      eyebrow: "Onboarding terminé",
      title: "Tout est prêt.",
      subtitle: (firstName: string, property: string, date: string) =>
        `Merci ${firstName}. Nous avons hâte de vous accueillir au ${property} le ${date}.`,
      identityDone: "Identité vérifiée",
      identityDoneCaption: "Votre document a bien été reçu",
      contractDone: "Contrat de location signé",
      contractDoneCaption: "Une copie est prête à télécharger",
      attestationDone: "Attestation signée",
      attestationDoneCaption: "Votre déclaration est jointe au contrat",
      checkInFrom: "Arrivée à partir de 15h00",
      calloutTag: "Après l’arrivée",
      calloutTitle: "Deux dernières étapes s’ouvriront",
      calloutBody:
        "Vérifiez l’état des lieux et transmettez votre attestation d’assurance habitation.",
      download: "Télécharger les documents signés",
      cta: "Aperçu du flux 2",
      restart: "Redémarrer l’aperçu",
    },

    inventoryPanel: {
      eyebrow: "Étape 5 sur 7 · Après l’arrivée",
      title: "Vérifiez l’état des lieux",
      subtitle:
        "Examinez les photos prises par notre équipe après le ménage, ajoutez vos propres photos si besoin, puis signez l’état des lieux.",
      noticeTitle: "Un état des lieux simplifié de l’état général du logement",
      noticeBody:
        "Cet état des lieux réduit vise à montrer l’état général de l’appartement à votre arrivée. Il ne recense pas chaque marque, trace d’usure ou imperfection mineure.",
      galleryTitle: "Photos d’inspection Europea",
      galleryCaption: (date: string) => `Prises le ${date} après le ménage`,
      awaitingPhotos:
        "Notre équipe n’a pas encore ajouté les photos d’inspection. Vous pourrez signer l’état des lieux dès qu’elles seront disponibles.",
      awaitingCheckIn: (date: string) =>
        `L’état des lieux constate l’état du logement à votre arrivée : il ne peut donc être signé qu’à partir du début de votre séjour, le ${date}.`,
      photoCount: (n: number) => `${n} photos`,
      morePhotos: (n: number) => `+${n} photos`,
      yourPhotos: "Vos photos complémentaires",
      yourPhotosCaption: (max: number) =>
        `Facultatif · Ajoutez jusqu’à ${max} photos prises à votre arrivée`,
      addPhotos: "Ajouter des photos",
      photosReady: (n: number) =>
        `${n} photo${n > 1 ? "s" : ""} prête${n > 1 ? "s" : ""} à joindre`,
      photosSubmitted: (n: number) =>
        `${n} photo${n > 1 ? "s" : ""} envoyée${n > 1 ? "s" : ""} avec votre état des lieux`,
      remove: "Retirer",
      confirm: (n: number) =>
        n === 0
          ? `Je confirme que cet état des lieux reflète l’état du logement tel que je l’ai trouvé à mon arrivée, et compris qu’il consigne l’état général du logement plutôt que chaque imperfection mineure.`
          : `Je confirme avoir examiné ${n === 1 ? "la photo d’inspection" : `les ${n} photos d’inspection`} et compris que cet état des lieux simplifié consigne l’état général du logement plutôt que chaque imperfection mineure.`,
      signCta: "Signer l’état des lieux",
      signatureDone: "État des lieux signé",
      back: "Récapitulatif d’arrivée",
      cta: "Continuer vers l’assurance",
      galleryClose: "Fermer la galerie",
      galleryPrev: "Photo précédente",
      galleryNext: "Photo suivante",
    },

    insurancePanel: {
      eyebrow: "Étape 6 sur 7 · Sous 10 jours",
      title: "Transmettez votre attestation d’assurance",
      subtitle:
        "Téléversez une preuve d’assurance habitation couvrant le logement pour toute la durée de votre séjour.",
      dueLabel: "Date limite du document",
      overdueLabel: "En retard",
      overdueBody: (days: number) =>
        `Cette attestation était attendue il y a ${days} jour${days === 1 ? "" : "s"}. Merci de l’envoyer dès que possible — vous pouvez toujours la déposer.`,
      dueTitle: "Sous 10 jours après votre arrivée",
      dueBody: (date: string) => `Votre date d’arrivée est le ${date}.`,
      requirementsTitle: "Votre attestation doit indiquer",
      requirementName: "Votre nom complet",
      requirementAddress: "L’adresse du logement loué",
      requirementDates: "Les dates de couverture de votre séjour",
      requirementLiability: "La responsabilité civile locative ou équivalent",
      uploadTitle: "Téléversez votre attestation d’assurance",
      uploadReady: "Document prêt",
      browse: "Parcourir les fichiers",
      replace: "Remplacer",
      note: "Votre attestation est conservée de manière sécurisée avec votre dossier de location et n’est accessible qu’à l’équipe de gestion.",
      back: "État des lieux",
      cta: "Terminer l’onboarding",
      // Superseded: the reference uses a compact upload row, not a dropzone.
      // dropTitle: "Déposez votre attestation ici",
    },

    homePanel: {
      eyebrow: "Bienvenue chez vous",
      title: "Votre dossier locataire est complet.",
      subtitle: (firstName: string, property: string, date: string) =>
        `Merci, ${firstName}. Nous avons hâte de vous accueillir à ${property} le ${date}.`,
      documentsDone: "Identité et documents de location",
      documentsDoneCaption: "Complétés avant l’arrivée",
      inventoryDone: "État des lieux",
      inventoryDoneCaption: (n: number) =>
        `${n} photos Europea examinées et signées`,
      insuranceDone: "Attestation d’assurance habitation",
      insuranceDoneCaption: "Attestation reçue",
      download: "Télécharger mes documents",
      missingContract: "votre contrat de location",
      missingInventory: "votre état des lieux",
      downloadIncomplete: (missing: string) =>
        `Nous n’avons pas pu préparer ${missing}. Le reste a bien été téléchargé — veuillez réessayer, et nous contacter si le problème persiste.`,
      cta: "Accéder à mon espace locataire",
      restart: "Revoir le flux 2",
    },

    signaturePanel: {
      eyebrow: "Signature électronique sécurisée",
      title: "Ajoutez votre signature",
      subtitlePrefix:
        "Signez dans le cadre avec votre doigt, un stylet ou votre souris. Votre signature sera apposée sur : ",
      instructionTitle: "Signez naturellement dans le cadre",
      instructionBody:
        "Pour un meilleur résultat, tournez votre téléphone à l’horizontale ou utilisez un stylet si vous en avez un.",
      placeholder: "Signez ici",
      clear: "Effacer",
      tenantName: "Locataire",
      dateOfSignature: "Date de signature",
      consent: (plural: boolean) =>
        `Je confirme que cette signature électronique manuscrite est la mienne et que j’ai l’intention de signer le${
          plural ? "s" : ""
        } document${plural ? "s" : ""} indiqué${plural ? "s" : ""} ci-dessus.`,
      confirm: "Confirmer la signature",
      confirming: "Enregistrement de votre signature…",
      saveFailed:
        "Votre signature n’a pas pu être enregistrée. Vérifiez votre connexion et confirmez à nouveau.",
      backToDocuments: "Retour aux documents",
      backToInventory: "Retour à l’état des lieux",
      labelBoth: "Contrat de location et attestation d’éligibilité",
      labelContract: "Contrat de location",
      labelInventory: "État des lieux",
    },
  },
};

export const STRINGS = { en, fr };
